import axios from 'axios';

// Get backend URL from environment variable
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Error handling helper
export const handleApiError = (error) => {
  if (error.response) {
    return error.response.data?.detail || error.response.data?.message || 'Erreur serveur';
  } else if (error.request) {
    return 'Impossible de contacter le serveur';
  } else {
    return error.message;
  }
};

// Authentication Service
export const authService = {
  // Register new club
  register: async (clubData) => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/register`, clubData);
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  },

  // Login club
  login: async (credentials) => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/login`, credentials);
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Check username availability
  checkUsername: async (username) => {
    try {
      const response = await axios.get(`${API_BASE}/api/auth/check-username/${username}`);
      return response.data;
    } catch (error) {
      console.error('Username check error:', error);
      throw error;
    }
  }
};

// PV Service (now with authentication)
export const pvService = {
  // Get all PVs for current club
  getAll: async () => {
    try {
      const response = await apiClient.get('/api/pvs');
      return response.data;
    } catch (error) {
      console.error('Error fetching PVs:', error);
      throw error;
    }
  },

  // Get PV by ID for current club
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/api/pvs/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching PV ${id}:`, error);
      throw error;
    }
  },

  // Create new PV for current club
  create: async (pvData) => {
    try {
      const response = await apiClient.post('/api/pvs', pvData);
      return response.data;
    } catch (error) {
      console.error('Error creating PV:', error);
      throw error;
    }
  },

  // Update PV for current club
  update: async (id, pvData) => {
    try {
      const response = await apiClient.put(`/api/pvs/${id}`, pvData);
      return response.data;
    } catch (error) {
      console.error(`Error updating PV ${id}:`, error);
      throw error;
    }
  },

  // Delete PV for current club
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/api/pvs/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting PV ${id}:`, error);
      throw error;
    }
  },

  // Export PV as PDF for current club
  exportPdf: async (id) => {
    try {
      console.log('🔄 Début du téléchargement PDF pour PV:', id);
      
      // Ajouter un timestamp pour éviter les problèmes de cache
      const timestamp = new Date().getTime();
      const response = await apiClient.get(`/api/pvs/${id}/export-pdf?t=${timestamp}`, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      console.log('📥 Réponse reçue:', {
        status: response.status,
        contentType: response.headers['content-type'],
        contentLength: response.headers['content-length'],
        contentDisposition: response.headers['content-disposition'],
        dataSize: response.data.size,
        dataType: response.data.type
      });
      
      // Vérifier que les données ne sont pas vides
      if (!response.data || response.data.size === 0) {
        throw new Error('Le fichier PDF reçu est vide');
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      console.log('📄 Blob créé:', {
        size: blob.size,
        type: blob.type
      });
      
      const url = window.URL.createObjectURL(blob);
      console.log('🔗 URL créée:', url);
      
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'PV_Tennis_Club.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      console.log('📁 Nom de fichier:', filename);
      
      link.download = filename;
      link.style.display = 'none'; // Cache le lien
      document.body.appendChild(link);
      
      console.log('🖱️ Déclenchement du clic...');
      link.click();
      
      // Solution alternative : ouvrir dans un nouvel onglet si le clic ne fonctionne pas
      setTimeout(() => {
        // Tenter d'ouvrir le PDF dans un nouvel onglet
        try {
          const newWindow = window.open(url, '_blank');
          if (newWindow) {
            console.log('🪟 PDF ouvert dans un nouvel onglet');
          } else {
            console.log('🚫 Popup bloquée, téléchargement direct seulement');
          }
        } catch (e) {
          console.log('⚠️ Erreur ouverture nouvel onglet:', e);
        }
      }, 500);
      
      // Attendre un peu avant de nettoyer
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        console.log('🧹 Nettoyage terminé');
      }, 1000);
      
      console.log('✅ Téléchargement initié avec succès');
      return { success: true, message: 'PDF téléchargé avec succès' };
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement PDF:', error);
      throw error;
    }
  },

  // Get ongoing tasks from latest PV for current club
  getOngoingTasks: async () => {
    try {
      // Use the backend endpoint that correctly handles both "En cours" and "En suspens" tasks
      const response = await apiClient.get(`/api/pvs/ongoing-tasks`);
      return response.data;
    } catch (error) {
      console.error('Error getting ongoing tasks:', error);
      return [];
    }
  },

  // Get untreated points from latest PV for current club
  getUntreatedPoints: async () => {
    try {
      const response = await apiClient.get(`/api/pvs/untreated-points`);
      return response.data;
    } catch (error) {
      console.error('Error fetching untreated points:', error);
      throw error;
    }
  },

  // Validate or lock a PV
  validatePv: async (id, status) => {
    try {
      const response = await apiClient.patch(`/api/pvs/${id}/validate`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error validating PV ${id}:`, error);
      throw error;
    }
  },

  // Send PV by email
  sendEmail: async (id) => {
    try {
      const response = await apiClient.post(`/api/pvs/${id}/send-email`);
      return response.data;
    } catch (error) {
      console.error(`Error sending PV ${id} by email:`, error);
      throw error;
    }
  }
};

// Members Service (now with authentication and club-specific)
export const memberService = {
  // Get all active members for current club
  getAll: async () => {
    try {
      const response = await apiClient.get('/api/members');
      return response.data;
    } catch (error) {
      console.error('Error fetching members:', error);
      throw error;
    }
  },

  // Get all members (active and inactive) for current club
  getAllMembers: async () => {
    try {
      const response = await apiClient.get('/api/members/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching all members:', error);
      throw error;
    }
  },

  // Get member by ID for current club
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/api/members/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching member ${id}:`, error);
      throw error;
    }
  },

  // Create new member for current club
  create: async (memberData) => {
    try {
      const response = await apiClient.post('/api/members', memberData);
      return response.data;
    } catch (error) {
      console.error('Error creating member:', error);
      throw error;
    }
  },

  // Update member for current club
  update: async (id, memberData) => {
    try {
      const response = await apiClient.put(`/api/members/${id}`, memberData);
      return response.data;
    } catch (error) {
      console.error(`Error updating member ${id}:`, error);
      throw error;
    }
  },

  // Delete member for current club
  delete: async (id) => {
    try {
      const response = await apiClient.delete(`/api/members/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting member ${id}:`, error);
      throw error;
    }
  }
};

// Club Service
export const clubService = {
  // Get club statistics
  getStatistics: async () => {
    try {
      const response = await apiClient.get('/api/club/statistics');
      return response.data;
    } catch (error) {
      console.error('Error fetching club statistics:', error);
      throw error;
    }
  }
};

// Admin Service (for admin users only)
export const adminService = {
  // Get admin statistics
  getStatistics: async () => {
    try {
      const response = await apiClient.get('/api/admin/statistics');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin statistics:', error);
      throw error;
    }
  },

  // Download complete backup
  downloadBackup: async () => {
    try {
      // Download MongoDB native backup as binary blob
      const response = await apiClient.get('/api/admin/download-backup', {
        responseType: 'blob'  // Important: receive as binary blob, not JSON
      });
      return response.data;  // Returns a Blob object
    } catch (error) {
      console.error('Error downloading backup:', error);
      throw error;
    }
  },

  // Restore from backup file
  restoreBackup: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/api/admin/restore-backup', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error restoring backup:', error);
      throw error;
    }
  },

  // Sync from production (automatic backup + restore)
  syncFromProduction: async () => {
    try {
      const response = await apiClient.post('/api/admin/sync-from-production');
      return response.data;
    } catch (error) {
      console.error('Error syncing from production:', error);
      throw error;
    }
  },

  // Reset TCCC password
  resetTcccPassword: async () => {
    try {
      const response = await apiClient.post('/api/admin/reset-tccc-password');
      return response.data;
    } catch (error) {
      console.error('Error resetting TCCC password:', error);
      throw error;
    }
  }
};

// Logo Service
export const logoService = {
  // Upload club logo
  upload: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/api/club/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  },

  // Get club logo URL
  get: async () => {
    try {
      const response = await apiClient.get('/api/club/logo');
      return response.data;
    } catch (error) {
      console.error('Error getting logo:', error);
      throw error;
    }
  },

  // Delete club logo
  delete: async () => {
    try {
      const response = await apiClient.delete('/api/club/logo');
      return response.data;
    } catch (error) {
      console.error('Error deleting logo:', error);
      throw error;
    }
  }
};

// Club Information Service
export const clubInfoService = {
  // Get club information
  get: async () => {
    try {
      const response = await apiClient.get('/api/club/info');
      return response.data;
    } catch (error) {
      console.error('Error getting club info:', error);
      throw error;
    }
  },

  // Update club information
  update: async (clubInfo) => {
    try {
      const response = await apiClient.put('/api/club/info', clubInfo);
      return response.data;
    } catch (error) {
      console.error('Error updating club info:', error);
      throw error;
    }
  },

  // Change club password
  changePassword: async (passwordData) => {
    try {
      const response = await apiClient.put('/api/club/password', passwordData);
      return response.data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }
};

// Password Reset Service (public)
export const passwordResetService = {
  // Request password reset
  requestReset: async (username) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        username: username
      });
      return response.data;
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw error;
    }
  },

  // Validate reset token
  validateToken: async (token) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error validating reset token:', error);
      throw error;
    }
  },

  // Reset password with token
  resetPassword: async (token, newPassword, confirmPassword) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        token: token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return response.data;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }
};

// Propositions Service
export const propositionsService = {
  // Créer une nouvelle session de collecte (authentifié)
  createSession: async (dateComiteCible) => {
    try {
      const response = await apiClient.post('/api/propositions/create-session', {
        date_comite_cible: dateComiteCible
      });
      return response.data;
    } catch (error) {
      console.error('Error creating proposition session:', error);
      throw error;
    }
  },

  // Récupérer la session active (authentifié)
  getActiveSession: async () => {
    try {
      const response = await apiClient.get('/api/propositions/active-session');
      return response.data;
    } catch (error) {
      console.error('Error getting active session:', error);
      throw error;
    }
  },

  // Envoyer email de rappel (authentifié)
  sendReminder: async () => {
    try {
      const response = await apiClient.post('/api/propositions/send-reminder');
      return response.data;
    } catch (error) {
      console.error('Error sending reminder email:', error);
      throw error;
    }
  },

  // Lister les propositions d'une session (authentifié)
  listPropositions: async (sessionToken) => {
    try {
      const response = await apiClient.get(`/api/propositions/list/${sessionToken}`);
      return response.data;
    } catch (error) {
      console.error('Error listing propositions:', error);
      throw error;
    }
  },

  // Récupérer informations d'une session (public)
  getSessionInfo: async (sessionToken) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/propositions/session/${sessionToken}`);
      return response.data;
    } catch (error) {
      console.error('Error getting session info:', error);
      throw error;
    }
  },

  // Clôturer une session (authentifié)
  closeSession: async (sessionToken) => {
    try {
      const response = await apiClient.post(`/api/propositions/close-session/${sessionToken}`);
      return response.data;
    } catch (error) {
      console.error('Error closing session:', error);
      throw error;
    }
  },

  // Soumettre plusieurs propositions (public)
  submitMultiplePropositions: async (sessionToken, propositionsData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/propositions/submit-multiple/${sessionToken}`, propositionsData);
      return response.data;
    } catch (error) {
      console.error('Error submitting multiple propositions:', error);
      throw error;
    }
  },

  // Soumettre une proposition (public) - gardé pour compatibilité
  submitProposition: async (sessionToken, proposition) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/propositions/submit/${sessionToken}`, {
        nom_membre: proposition.nom_membre,
        email_membre: proposition.email_membre,
        titre: proposition.titre,
        description: proposition.description
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting proposition:', error);
      throw error;
    }
  },

  // Marquer les propositions comme intégrées dans un PV (authentifié)
  markPropositionsAsIntegrated: async (propositionIds) => {
    try {
      const response = await apiClient.post('/api/propositions/mark-integrated', {
        proposition_ids: propositionIds
      });
      return response.data;
    } catch (error) {
      console.error('Error marking propositions as integrated:', error);
      throw error;
    }
  },

  // Prendre une décision sur une proposition (authentifié)
  makePropositionDecision: async (propositionId, statut, decisionRemarques = '') => {
    try {
      const response = await apiClient.post(`/api/propositions/${propositionId}/decision`, {
        statut: statut,
        decision_remarques: decisionRemarques
      });
      return response.data;
    } catch (error) {
      console.error('Error making proposition decision:', error);
      throw error;
    }
  },

  // Prendre des décisions en lot sur plusieurs propositions (authentifié)
  makeBatchPropositionDecisions: async (decisions) => {
    try {
      const response = await apiClient.post('/api/propositions/decisions-batch', {
        decisions: decisions  // [{id: int, statut: str, decision_remarques: str}]
      });
      return response.data;
    } catch (error) {
      console.error('Error making batch proposition decisions:', error);
      throw error;
    }
  }
};

// PV Sharing Service
export const pvSharingService = {
  // Generate share link for a PV
  generateShareLink: async (pvId) => {
    try {
      const response = await apiClient.post(`/api/pvs/${pvId}/share`);
      return response.data;
    } catch (error) {
      console.error('Error generating share link:', error);
      throw error;
    }
  },

  // Revoke share link for a PV
  revokeShareLink: async (pvId) => {
    try {
      const response = await apiClient.delete(`/api/pvs/${pvId}/share`);
      return response.data;
    } catch (error) {
      console.error('Error revoking share link:', error);
      throw error;
    }
  },

  // Verify password and get shared PV
  verifySharedPV: async (shareToken, password) => {
    try {
      const response = await axios.post(
        `${API_BASE}/api/shared/${shareToken}/verify`,
        { password }
      );
      return response.data;
    } catch (error) {
      console.error('Error verifying shared PV:', error);
      throw error;
    }
  },

  // Get shared PV data (after authentication)
  getSharedPV: async (shareToken) => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/shared/${shareToken}`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting shared PV:', error);
      throw error;
    }
  },

  // Get members for shared PV
  getMembersForSharedPV: async (shareToken) => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/shared/${shareToken}/members`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting members for shared PV:', error);
      throw error;
    }
  },

  // Add proposition to shared PV
  addPropositionToSharedPV: async (shareToken, proposition) => {
    try {
      const response = await axios.post(
        `${API_BASE}/api/shared/${shareToken}/add-proposition`,
        proposition
      );
      return response.data;
    } catch (error) {
      console.error('Error adding proposition to shared PV:', error);
      throw error;
    }
  },
};